import { useCallback, useMemo, useEffect, useRef } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { STEPS } from '../constants';

const MAX_CULTURES = 10;

/**
 * Hook for step validation and URL security
 *
 * @param {Object} selections - Current form selections
 * @param {number} currentStep - Current step from URL
 * @param {Function} setUrlState - Function to update URL state
 * @returns {Object} Validation functions and state
 */
export function useStepValidation(selections, currentStep, setUrlState) {
  const { t } = useTranslation('riskCalculator');
  const hasShownWarningRef = useRef(false);

  // Validate if a step is complete
  const isStepValid = useCallback(
    (step) => {
      switch (step) {
        case STEPS.CULTURES:
          return (
            selections.cultures.length > 0 &&
            selections.cultures.length <= MAX_CULTURES
          );
        case STEPS.TREATMENT_TYPE:
          return selections.treatmentType !== null;
        case STEPS.APPLICATION_TECHNIQUE:
          return selections.applicationTechnique !== null;
        case STEPS.COMMERCIAL_PRODUCTS:
          return (
            selections.commercialProducts.length > 0 &&
            selections.commercialProducts.every((p) => p.dose && p.dose > 0 && p.unit)
          );
        default:
          return false;
      }
    },
    [selections]
  );

  // Can proceed to next step?
  const canProceedToNextStep = useMemo(() => {
    return isStepValid(currentStep);
  }, [currentStep, isStepValid]);

  // Can calculate risks?
  const canCalculate = useMemo(() => {
    return (
      isStepValid(STEPS.CULTURES) &&
      isStepValid(STEPS.TREATMENT_TYPE) &&
      isStepValid(STEPS.APPLICATION_TECHNIQUE) &&
      isStepValid(STEPS.COMMERCIAL_PRODUCTS)
    );
  }, [isStepValid]);

  // Calculate max accessible step
  const getMaxAccessibleStep = useCallback(() => {
    let maxStep = STEPS.CULTURES;

    if (isStepValid(STEPS.CULTURES)) {
      maxStep = STEPS.TREATMENT_TYPE;
    }
    if (isStepValid(STEPS.CULTURES) && isStepValid(STEPS.TREATMENT_TYPE)) {
      maxStep = STEPS.APPLICATION_TECHNIQUE;
    }
    if (
      isStepValid(STEPS.CULTURES) &&
      isStepValid(STEPS.TREATMENT_TYPE) &&
      isStepValid(STEPS.APPLICATION_TECHNIQUE)
    ) {
      maxStep = STEPS.COMMERCIAL_PRODUCTS;
    }

    return maxStep;
  }, [isStepValid]);

  // Show warning with debounce
  const showWarning = useCallback(
    (messageKey) => {
      if (!hasShownWarningRef.current) {
        message.warning(t(messageKey));
        hasShownWarningRef.current = true;
        setTimeout(() => {
          hasShownWarningRef.current = false;
        }, 3000);
      }
    },
    [t]
  );

  // Validate step from URL (security check)
  useEffect(() => {
    // Check if step is within valid range
    if (currentStep < STEPS.CULTURES || currentStep > STEPS.COMMERCIAL_PRODUCTS) {
      showWarning('validation_step_out_of_range');
      setUrlState({ step: STEPS.CULTURES });
      return;
    }

    // Check if user is trying to skip steps
    if (currentStep > STEPS.CULTURES) {
      const maxAccessibleStep = getMaxAccessibleStep();

      if (currentStep > maxAccessibleStep) {
        showWarning('validation_complete_previous_steps');
        setUrlState({ step: maxAccessibleStep });
      }
    }
  }, [currentStep, getMaxAccessibleStep, setUrlState, showWarning]);

  // Validate cultures count from URL
  useEffect(() => {
    if (selections.cultures && selections.cultures.length > MAX_CULTURES) {
      const truncatedCultures = selections.cultures.slice(0, MAX_CULTURES);
      setUrlState({ cultures: truncatedCultures });
      showWarning('validation_max_cultures');
    }
  }, [selections.cultures, setUrlState, showWarning]);

  return {
    isStepValid,
    canProceedToNextStep,
    canCalculate,
    getMaxAccessibleStep,
    MAX_CULTURES,
  };
}

export default useStepValidation;
